import NewDesign from 'components/NewDesign';
import { cn } from 'helpers/css';
import { isArray, isEmpty, isPlainObject, lowerCase } from 'lodash';
import { Check, PlusCircle } from 'lucide-react';
import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from 'shadcn/components/command';
import SelectFetchingData from '../SelectFetchingData';

/**
 *
 * @param {Object} props
 * @param {"array" | "object" | "string" | "number"} props.selected
 * @param {{ "label": any, value: "string" | number | boolean }} props.options
 * @param {boolean} props.isLoading
 * @param {boolean} props.isMulti
 * @param {(option, isSelected) => void} props.onChangeSelect
 * @param {(inputSearch: string) => void} props.onChangeInputSearch
 * @returns
 */
const SelectOptions = ({
  selected,
  options,
  placeholder,
  isLoading,
  isMulti,
  onChangeInputSearch,
  onChangeSelect,
  onCreateNewOption,
  isCreatable = false,
}) => {
  const [inputSearch, setInputSearch] = useState('');

  useEffect(() => {
    return () => {
      setInputSearch('');
    };
  }, []);

  const showItems = useCallback(
    (option) => {
      const isSelected = isMulti
        ? (selected || []).some((el) => el.value === option.value)
        : isPlainObject(selected)
        ? selected.value === option.value
        : selected === option.value;

      return (
        <CommandItem
          key={option.value}
          className={cn(
            !isMulti &&
              isSelected &&
              'hover:tw-bg-primary tw-bg-primary tw-text-[var(--color-white-v2)] data-[selected=true]:tw-text-[var(--color-white-v2)]'
          )}
          onSelect={() => onChangeSelect(option, isSelected)}
        >
          {!isMulti ? (
            <></>
          ) : (
            <div
              className={cn(
                'tw-flex tw-size-4 tw-items-center tw-justify-center tw-rounded-sm tw-border tw-border-primary',
                isSelected
                  ? 'tw-bg-primary tw-text-primary-foreground'
                  : 'tw-opacity-50 [&_svg]:tw-invisible'
              )}
            >
              <Check />
            </div>
          )}
          {option.icon && <option.icon />}
          <span className='tw-truncate'>{option.label}</span>
          {option.count && (
            <span className='tw-ml-auto tw-font-mono tw-text-xs'>{option.count}</span>
          )}
          {!isMulti && (
            <div
              className={cn(
                'tw-flex tw-size-4 tw-items-center tw-justify-center tw-ml-auto',
                isSelected ? '' : '[&_svg]:tw-invisible'
              )}
            >
              <Check />
            </div>
          )}
        </CommandItem>
      );
    },
    [selected]
  );

  const filterItemsNotSelected = useCallback(
    (option) => {
      const isSelected = isMulti
        ? (selected || []).some((el) => el.value === option.value)
        : isPlainObject(selected)
        ? selected.value === option.value
        : selected === option.value;

      if (isMulti) {
        return !isSelected;
      }

      return true;
    },
    [selected, isMulti]
  );

  const filterByInputSearch = useCallback(
    (option) => {
      if (inputSearch === '') {
        return true;
      }

      return lowerCase(option.label).includes(lowerCase(inputSearch));
    },
    [inputSearch]
  );

  // Combine options with selected custom options that might not be in the original options
  const allOptions = useMemo(() => {
    const customOptions = isArray(selected) ? selected.filter((item) => item.isCustom) : [];

    return [...options, ...customOptions];
  }, [options, selected]);

  const optionsNotSelectedYet = useMemo(() => {
    return allOptions.length > 0
      ? allOptions.filter(filterItemsNotSelected).filter(filterByInputSearch)
      : [];
  }, [allOptions, filterItemsNotSelected, filterByInputSearch]);

  const optionsSelected = useMemo(() => {
    if (isPlainObject(selected) && !isEmpty(selected)) {
      return [selected];
    }

    if (!isArray(selected)) {
      return [];
    }

    return selected.filter(filterByInputSearch);
  }, [selected, filterByInputSearch]);

  const shouldShowCreateOption = useMemo(() => {
    if (!isCreatable || !inputSearch.trim()) return false;

    // Check if the search term matches any existing option
    const hasMatchingOption = allOptions.some((option) =>
      lowerCase(option.label).includes(lowerCase(inputSearch))
    );

    return !hasMatchingOption;
  }, [isCreatable, inputSearch, allOptions]);

  const handleCreateNewOption = () => {
    if (onCreateNewOption && inputSearch.trim()) {
      const newOption = {
        label: inputSearch.trim(),
        value: inputSearch.trim(),
        __isNew__: true,
      };
      onCreateNewOption(newOption);
    }
  };

  return (
    <Command shouldFilter={false}>
      <CommandInput
        value={inputSearch}
        onValueChange={(value) => {
          onChangeInputSearch && onChangeInputSearch(value);
          setInputSearch(value);
        }}
        placeholder={placeholder || 'Typing to search...'}
      />
      <CommandList className='tw-max-h-full'>
        {!isLoading && optionsSelected.length <= 0 && optionsNotSelectedYet.length <= 0 && (
          <CommandEmpty>No results found.</CommandEmpty>
        )}

        <Fragment>
          {isMulti && isArray(optionsSelected) && optionsSelected.length > 0 && (
            <CommandGroup
              heading='Selected'
              className='tw-max-h-[18.75rem] tw-overflow-y-auto tw-overflow-x-hidden'
            >
              {optionsSelected.map(showItems)}
            </CommandGroup>
          )}

          {isMulti &&
            !isLoading &&
            optionsSelected.length > 0 &&
            optionsNotSelectedYet.length > 0 && <NewDesign.Separator className='tw-my-2' />}

          {!isLoading && optionsNotSelectedYet.length > 0 && (
            <CommandGroup
              heading='Options'
              className='tw-max-h-[18.75rem] tw-overflow-y-auto tw-overflow-x-hidden'
            >
              {optionsNotSelectedYet.map(showItems)}
            </CommandGroup>
          )}

          {shouldShowCreateOption && (
            <CommandGroup>
              <CommandItem onSelect={handleCreateNewOption}>
                <div className='tw-flex tw-items-center tw-gap-2'>
                  <PlusCircle className='tw-h-4 tw-w-4' />
                  <span>Create "{inputSearch}"</span>
                </div>
              </CommandItem>
            </CommandGroup>
          )}

          {isLoading && <SelectFetchingData />}
        </Fragment>
      </CommandList>
    </Command>
  );
};

export default SelectOptions;
